import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import { useNavigate } from 'react-router-dom';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { SelectFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import onboardingTemplatesService from '../../services/onboardingTemplates/onboardingTemplatesService';
import onboardingTemplateService from '../../services/onboardingTemplates/onboardingTemplateService';
import applyOnboardingTemplateService from '../../services/onboardingTemplates/applyOnboardingTemplateService';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import TemplateInfo from '../../../../portal/community/members/components/TemplateInfo';

const FORM_NAME = 'applyOnboardingTemplate';
const REQUIRED_FIELDS = ['onboardingTemplateId'];

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);
  return errors;
};

const DEFAULT_DATA = { onboardingTemplateId: '' };

const ApplyOnboardingTemplateForm = ({
  open,
  returnRoute,
  dispatchCommunityOnboardingTemplatesGet,
  communityOnboardingTemplatesFetch,
  dispatchApplyOnboardingTemplatePost,
  applyOnboardingTemplateFetch,
  dispatchOnboardingTemplateGet,
  onboardingTemplateFetch,
  communityId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { actionId } = useParams();

  const onboardingTemplateId = useSelector(state =>
    get(state, `form.${FORM_NAME}.values.onboardingTemplateId`)
  );

  useEffect(() => {
    if (communityId) dispatchCommunityOnboardingTemplatesGet(communityId, t);
  }, [communityId, t, dispatchCommunityOnboardingTemplatesGet]);

  useEffect(() => {
    if (onboardingTemplateId) dispatchOnboardingTemplateGet(onboardingTemplateId, undefined, t);
  }, [onboardingTemplateId, dispatchOnboardingTemplateGet, t]);

  const options = useMemo(
    () =>
      (communityOnboardingTemplatesFetch.value || []).map(o => ({
        key: o.id,
        value: o.id,
        text: o.name,
      })),
    [communityOnboardingTemplatesFetch.value]
  );

  const handleApplyWorklist = useCallback(
    body =>
      dispatchApplyOnboardingTemplatePost(
        communityId,
        actionId,
        body,
        navigate(returnRoute),
        t
      ),
    [returnRoute, communityId, actionId, t, dispatchApplyOnboardingTemplatePost, navigate]
  );

  if (!open) return null;

  return (
    <FormModal
      t={t}
      header={t('applyOnboardingTemplate')}
      dataLoading={false}
      submitProcessing={applyOnboardingTemplateFetch.pending}
      onSubmit={handleApplyWorklist}
      validate={validate}
      form={FORM_NAME}
      data={DEFAULT_DATA}
      cancelRoute={returnRoute}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="save"
      saveButtonContent={t('save')}
    >
      <Grid>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            fluid
            search
            clearable
            label={t('selectAnOnboardingTemplate')}
            name="onboardingTemplateId"
            loading={communityOnboardingTemplatesFetch.pending}
            options={options}
            valueComponent={SelectFieldValueComponent}
          />
        </Grid.Column>
      </Grid>
      {onboardingTemplateId && onboardingTemplateFetch.value && (
        <TemplateInfo template={onboardingTemplateFetch.value} />
      )}
    </FormModal>
  );
};

ApplyOnboardingTemplateForm.propTypes = {
  dispatchApplyOnboardingTemplatePost: PropTypes.func.isRequired,
  applyOnboardingTemplateFetch: PropTypes.object.isRequired,
  dispatchOnboardingTemplateGet: PropTypes.func.isRequired,
  onboardingTemplateFetch: PropTypes.object.isRequired,
  dispatchCommunityOnboardingTemplatesGet: PropTypes.func.isRequired,
  communityOnboardingTemplatesFetch: PropTypes.object.isRequired,
  returnRoute: PropTypes.string.isRequired,
  communityId: PropTypes.string.isRequired,
  open: PropTypes.bool,
};
ApplyOnboardingTemplateForm.defaultProps = { open: false };

export default compose(
  applyOnboardingTemplateService,
  onboardingTemplateService,
  onboardingTemplatesService
)(ApplyOnboardingTemplateForm);
